import { getLocalizedOrDefault } from "../../../helpers/Functions";

const AttributesConfig = {
  collection: "attributes",
  fields: [
    "id",
    "name",
    "input_type",
    "insert_thousands_separator",
    "default_value",
    "symbol_placement",
    "groupings.logic_builder_id.id",
    "qualifying_disclaimer.id",

    "locale.languages_code.code",
    "locale.language",
    "locale.label",
    "locale.value_is_approximation",
    "locale.choices",
    "locale.units",
    "locale.facets",
    "locale.value_style.id",
    "locale.unit_style.id",
    "locale.facet_style.id",
    "locale.unit_separator",
    "locale.value_separator",
    "locale.facet_separator",
    "locale.facet_placement"
  ],

  adapter: class Attribute {
    constructor(feature) {
      this.id = feature.id;
      this.name = feature.name;
      this.input_type = feature.input_type;
      this.insert_thousands_separator = !!feature.insert_thousands_separator;
      this.default_value = feature.default_value || "";
      this.symbol_placement = feature.symbol_placement;
      /* Options:
        - spec-ind: After Each Specification
        - spec-end: After All Specifications
        - group-end: After Grouping Label
      */
      this.symbol_placement = feature.symbol_placement || "spec-ind";
      this.place_symbol_after_each_spec = this.symbol_placement === "spec-ind";
      this.place_symbol_after_all_specs = this.symbol_placement === "spec-end";
      this.place_symbol_after_grouping_label =
        this.symbol_placement === "group-end";

      this.groupings = (feature.groupings || []).map(
        ({ logic_builder_id: { id } }) => id
      );
      this.qualifying_disclaimer = feature.qualifying_disclaimer?.id;

      // Input types that are defined at the product level as opposed to feature level
      let product_value_input_types = [
        "text-input",
        "numeric",
        "currency-display"
      ];
      this.values_come_from_products =
        product_value_input_types.indexOf(this.input_type) > -1;

      let unit_input_types = ["text-input", "numeric"];
      this.uses_unit = unit_input_types.indexOf(this.input_type) > -1;

      let locale = feature.locale?.[0];
      this.locale = feature?.locale;
      this.label = locale?.label || this.name;

      this.value_is_approximation = locale?.value_is_approximation;
      this.unit_style = locale?.unit_style?.id;
      this.facet_style = locale?.facet_style?.id;
      this.value_style = locale?.value_style?.id;
      this.facets = (locale?.facets || []).map((f) => ({
        ...f,
        display_value: f.shortname || f.value
      }));
      this.units = locale?.units || [];
      this.choices = locale?.choices || [];
      this.value_separator = locale?.value_separator || "";
      this.facet_placement = locale?.facet_placement || "after";
      this.facet_separator = locale?.facet_separator || "";
      this.unit_separator = locale?.unit_separator || "";
    }

    map(collections, maps) {
      this.locale = this.locale.map(
        ({ languages_code, language, ...other_props }) => ({
          ...other_props,
          language:
            maps.languages?.[languages_code?.code] ||
            collections.languages?.find(
              ({ language: lang }) => languages_code?.language === lang
            ) ||
            //scanners
            maps.locales?.[language?.id || language]
        })
      );

      this.qualifying_disclaimer = maps.disclaimers[this.qualifying_disclaimer];
      this.value_style =
        maps.attribute_styles?.[this.value_style] ||
        maps.specification_styles?.[this.value_style] ||
        "regular";
      this.unit_style =
        maps.attribute_styles?.[this.unit_style] ||
        maps.specification_styles?.[this.unit_style] ||
        "regular";
      this.facet_style =
        maps.attribute_styles?.[this.facet_style] ||
        maps.specification_styles?.[this.facet_style] ||
        "regular";
      this.groupings = this.groupings.map((id) => maps.logic_builder[id]);

      const { global_settings } = collections;
      const { default_locale } = global_settings;

      // e.g. field_name: header
      //      selected_locale: 'es-CL'
      this.getLocalized = (field_name, selected_locale) => {
        return getLocalizedOrDefault(
          this,
          field_name,
          { locale: selected_locale },
          { locale: default_locale.language }
        );
      };
    }
  }
};

export default AttributesConfig;
